import React, { useEffect, useState } from "react";
import {
  Search,
  Download,
  RefreshCw,
  Calendar,
  Users,
  Mail,
  Phone,
  Filter,
  X,
  ChevronDown,
  Ban,
  LogIn,
  LogOut,
  AlertTriangle,
} from "lucide-react";
import AdminUpdates from "./Components/AdminUpdates";

const ADMIN_EMAIL = "admin@gmail.com";
const ADMIN_PASSWORD = "12345";

function BookingTable() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [roomTypeFilter, setRoomTypeFilter] = useState("all");
  const [occupancyFilter, setOccupancyFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const authStatus = localStorage.getItem("isAdmin") === "true";
    setIsAuthenticated(authStatus);
    if (authStatus) fetchBookings();
  }, []);

  const handleAdminLogin = (e) => {
    e.preventDefault();
    setLoginError("");

    if (adminEmail.trim() === ADMIN_EMAIL && adminPassword === ADMIN_PASSWORD) {
      localStorage.setItem("isAdmin", "true");
      setIsAuthenticated(true);
      setAdminEmail("");
      setAdminPassword("");
      fetchBookings();
    } else {
      setLoginError("Invalid email or password");
    }
  };

  const handleLogout = () => {
    if (!window.confirm("Are you sure you want to logout?")) return;
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("adminRoute");
    setIsAuthenticated(false);
    setBookings([]);
    setFilteredBookings([]);
  };

  const sampleData = [
    {
      id: 1,
      booking_id: "BK-00001",
      active: true,
      Name: "Bhagavath",
      Phone: "7894561230",
      Email: "bhagavath.1997@gmail.com",
      RoomType: "Suite Room",
      occupancyType: "Single",
      CheckIn: "2025-12-31T14:00:00",
      CheckOut: "2026-01-02T11:00:00",
      Timestamp: "2025-12-30T10:00:00",
    },
    {
      id: 2,
      booking_id: "BK-00002",
      active: true,
      Name: "Karthik S",
      Phone: "7397109901",
      Email: "2001karthicksrini@gmail.com",
      RoomType: "Standard Room",
      occupancyType: "Single",
      CheckIn: "2025-12-30T14:00:00",
      CheckOut: "2025-12-31T11:00:00",
      Timestamp: "2025-12-30T12:30:00",
    },
  ];

  const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return isNaN(date.getTime())
      ? value
      : date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  const fetchBookings = async (retryCount = 3) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("https://karunaregent.com/karuna_api/get_booking_details");
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();

      if (result.status_code !== "200" || !Array.isArray(result.details)) {
        throw new Error("Invalid or empty response from server");
      }

      const mappedData = result.details.map((item) => ({
        id: item.id,
        booking_id: item.booking_id || `BK-${String(item.id).padStart(5, "0")}`,
        active: item.active === true || item.active === "true",
        Name: item.full_name || "—",
        Phone: item.ph_no || "—",
        Email: item.email || "—",
        RoomType: item.room_type || "—",
        occupancyType: item.occupancy === "1" ? "Single" : item.occupancy === "2" ? "Double" : "Other",
        CheckIn: item.checkin ? `${item.checkin}T14:00:00` : null,
        CheckOut: item.checkout ? `${item.checkout}T11:00:00` : null,
        Timestamp: item["booking date"] ? `${item["booking date"]}T00:00:00` : new Date().toISOString(),
        status: item.status || null,
      }));

      setBookings(mappedData);
    } catch (err) {
      console.error("Fetch Error:", err);
      if (retryCount > 0) {
        setTimeout(() => fetchBookings(retryCount - 1), 2000);
      } else {
        setError("Failed to load bookings from server. Showing sample data.");
        setBookings(sampleData);
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (booking) => {
    if (!window.confirm(`Cancel booking ${booking.booking_id} for ${booking.Name}?`)) return;

    setActionLoading(booking.id);

    try {
      const response = await fetch("https://karunaregent.com/karuna_api/update_booking_status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: String(booking.id),
          booking_id: booking.booking_id,
          status: false,
          reason: "cancel",
        }),
      });

      if (!response.ok) throw new Error(await response.text());

      alert(`Booking ${booking.booking_id} cancelled successfully.`);
      await fetchBookings();
    } catch (err) {
      alert("Failed to cancel: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const checkoutBooking = async (booking) => {
    if (!window.confirm(`Check out ${booking.booking_id} for ${booking.Name}?`)) return;

    setActionLoading(booking.id);

    try {
      const response = await fetch("https://karunaregent.com/karuna_api/update_booking_status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: String(booking.id),
          booking_id: booking.booking_id,
          status: true,
          reason: "checkout",
        }),
      });

      if (!response.ok) throw new Error(await response.text());

      alert(`Guest ${booking.Name} checked out successfully.`);
      await fetchBookings();
    } catch (err) {
      alert("Failed to check out: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    let result = [...bookings];

    if (searchTerm) {
      result = result.filter((b) =>
        Object.values(b).some((v) => String(v || "").toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (roomTypeFilter !== "all") {
      result = result.filter((b) => b.RoomType?.toLowerCase() === roomTypeFilter.toLowerCase());
    }
    if (occupancyFilter !== "all") {
      result = result.filter((b) => b.occupancyType?.toLowerCase() === occupancyFilter.toLowerCase());
    }

    if (dateFilter !== "all") {
      const now = new Date();
      result = result.filter((b) => {
        const ci = new Date(b.CheckIn);
        if (isNaN(ci)) return false;
        if (dateFilter === "upcoming") return ci > now;
        if (dateFilter === "past") return ci < now;
        if (dateFilter === "today") return ci.toDateString() === now.toDateString();
        return true;
      });
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        let x = a[sortConfig.key] ?? "";
        let y = b[sortConfig.key] ?? "";
        if (typeof x === "string") x = x.toLowerCase();
        if (typeof y === "string") y = y.toLowerCase();
        return (x < y ? -1 : x > y ? 1 : 0) * (sortConfig.direction === "asc" ? 1 : -1);
      });
    }

    setFilteredBookings(result);
    setCurrentPage(1);
  }, [bookings, searchTerm, roomTypeFilter, occupancyFilter, dateFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const exportToCSV = () => {
    const headers = ["Booking ID", "Status", "Name", "Phone", "Email", "Room Type", "Occupancy", "Check-In", "Check-Out", "Booked On"];
    const rows = filteredBookings.map((b) => [
      b.booking_id,
      b.active ? "Active" : b.status === "checkout" ? "Checked Out" : "Cancelled",
      b.Name,
      b.Phone,
      b.Email,
      b.RoomType,
      b.occupancyType,
      formatDateTime(b.CheckIn),
      formatDateTime(b.CheckOut),
      formatDateTime(b.Timestamp),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${(cell ?? "").toString().replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `karuna_regent_bookings_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setRoomTypeFilter("all");
    setOccupancyFilter("all");
    setDateFilter("all");
    setSortConfig({ key: null, direction: "asc" });
  };

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, startIndex + itemsPerPage);

  const roomTypes = [...new Set(bookings.map((b) => b.RoomType).filter(Boolean))];
  const occupancyTypes = [...new Set(bookings.map((b) => b.occupancyType).filter(Boolean))];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8">
          <div className="text-center mb-8">
            <LogIn className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
            <p className="text-gray-600 mt-2">Karuna Regent Hotel Management</p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                placeholder="admin@gmail.com"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                <AlertTriangle size={16} />
                {loginError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Sign In
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Protected area — only authorized personnel
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Booking Management</h1>
              <p className="text-gray-600 mt-1">Karuna Regent Hotel System</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchBookings}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <X className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Users className="w-5 h-5" />
                <span className="text-sm font-medium">Total Bookings</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{bookings.length}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <Calendar className="w-5 h-5" />
                <span className="text-sm font-medium">Filtered</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{filteredBookings.length}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-purple-600 mb-1">
                <Mail className="w-5 h-5" />
                <span className="text-sm font-medium">Room Types</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">{roomTypes.length}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-orange-600 mb-1">
                <Phone className="w-5 h-5" />
                <span className="text-sm font-medium">Page</span>
              </div>
              <div className="text-2xl font-bold text-orange-900">
                {currentPage}/{totalPages || 1}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center gap-2">
            <X className="w-5 h-5 text-yellow-600" />
            <p className="text-yellow-800">{error}</p>
          </div>
        )}

        <AdminUpdates />

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters & Search
            </h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 text-blue-600"
            >
              <ChevronDown className={`w-5 h-5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>

          <div className={`space-y-4 ${showFilters ? "block" : "hidden md:block"}`}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, phone, booking ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                value={roomTypeFilter}
                onChange={(e) => setRoomTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Room Types</option>
                {roomTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <select
                value={occupancyFilter}
                onChange={(e) => setOccupancyFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Occupancy</option>
                {occupancyTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Dates</option>
                <option value="upcoming">Upcoming</option>
                <option value="today">Today</option>
                <option value="past">Past</option>
              </select>

              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
              <button
                onClick={exportToCSV}
                disabled={filteredBookings.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400"
              >
                <Download className="w-4 h-4" />
                Export CSV ({filteredBookings.length})
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-10 h-10 text-blue-600 animate-spin" />
              <span className="ml-4 text-lg text-gray-600">Loading bookings...</span>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-600">No bookings found</p>
              <p className="text-gray-500 mt-2">Try adjusting filters or refresh the data</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Booking ID
                      </th>
                      {["Name", "Phone", "Email", "Room Type", "Occupancy", "Check-In", "Check-Out", "Booked On"].map(
                        (header, i) => {
                          const keys = ["Name", "Phone", "Email", "RoomType", "occupancyType", "CheckIn", "CheckOut", "Timestamp"];
                          const key = keys[i];
                          return (
                            <th
                              key={header}
                              onClick={() => handleSort(key)}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            >
                              <div className="flex items-center gap-1">
                                {header}
                                {sortConfig.key === key && (
                                  <span className="text-blue-600">{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
                                )}
                              </div>
                            </th>
                          );
                        }
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedBookings.map((booking, idx) => (
                      <tr key={booking.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              booking.active && booking.status === "checkout"
                                ? "bg-blue-100 text-blue-800"
                                : booking.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {booking.active && booking.status === "checkout"
                              ? "Checked Out"
                              : booking.active
                              ? "Active"
                              : "Cancelled"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {booking.booking_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {booking.Name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{booking.Phone}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{booking.Email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {booking.RoomType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            {booking.occupancyType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {formatDateTime(booking.CheckIn)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {formatDateTime(booking.CheckOut)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateTime(booking.Timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {booking.active && booking.status === "checkout" ? (
                            <span className="text-blue-600 text-sm font-medium flex items-center gap-1.5">
                              <LogOut className="w-4 h-4" />
                              Checked Out
                            </span>
                          ) : !booking.active ? (
                            <span className="text-gray-500 text-sm italic">Cancelled</span>
                          ) : (
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => cancelBooking(booking)}
                                disabled={actionLoading === booking.id}
                                className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-70 shadow-sm"
                              >
                                <Ban className="w-4 h-4" />
                                {actionLoading === booking.id ? "Cancelling..." : "Cancel"}
                              </button>

                              <button
                                onClick={() => checkoutBooking(booking)}
                                disabled={actionLoading === booking.id}
                                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-70 shadow-sm"
                              >
                                <LogOut className="w-4 h-4" />
                                {actionLoading === booking.id ? "Checking out..." : "Check-out"}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 gap-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                  <span className="font-medium">
                    {Math.min(startIndex + itemsPerPage, filteredBookings.length)}
                  </span>{" "}
                  of <span className="font-medium">{filteredBookings.length}</span> results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1 + Math.max(0, currentPage - 3);
                    if (pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-4 py-2 border rounded-lg text-sm font-medium transition ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-gray-300 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingTable;